import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, MessageCircle, ExternalLink, ArrowLeft, Heart, Shield, Users } from 'lucide-react';

const CRISIS_RESOURCES = [
  {
    name: '988 Suicide & Crisis Lifeline',
    description: 'Free, confidential support 24/7 for anyone in distress',
    phone: '988',
    textNumber: '988',
    textKeyword: null,
    icon: Heart,
    color: '#C9A84C',
    priority: true
  },
  {
    name: 'Crisis Text Line',
    description: 'Free crisis counseling via text message',
    phone: null,
    textNumber: '741741',
    textKeyword: 'HOME',
    icon: MessageCircle,
    color: '#A9756B'
  },
  {
    name: 'National Domestic Violence Hotline',
    description: 'Support for survivors of domestic violence',
    phone: '1-800-799-7233',
    textNumber: '88788',
    textKeyword: 'START',
    icon: Shield,
    color: '#72826F'
  },
  {
    name: 'RAINN',
    description: 'Support for survivors of sexual violence',
    phone: '1-800-656-4673',
    textNumber: null,
    textKeyword: null,
    icon: Users,
    color: '#b8a9c9'
  },
  {
    name: 'SAMHSA National Helpline',
    description: 'Treatment referral and information service',
    phone: '1-800-662-4357',
    textNumber: null,
    textKeyword: null,
    icon: Heart,
    color: '#a9c9b8'
  }
];

const CrisisResources = ({ isModal = false, onClose }) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const handleCall = (phone) => {
    window.location.href = `tel:${phone.replace(/-/g, '')}`;
  };

  const handleText = (number, keyword) => {
    const body = keyword ? `?body=${keyword}` : '';
    window.location.href = `sms:${number}${body}`;
  };

  return (
    <div 
      className={`min-h-screen ${isModal ? 'fixed inset-0 z-50 overflow-y-auto' : ''}`}
      style={{ backgroundColor: '#FBF9F6' }}
      data-testid="crisis-resources-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-10 px-4 py-4" style={{ backgroundColor: 'rgba(251,249,246,0.95)', backdropFilter: 'blur(10px)' }}>
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-2 rounded-full transition-colors"
            style={{ backgroundColor: '#F7F1EE' }}
            data-testid="crisis-back-btn"
          >
            <ArrowLeft className="w-5 h-5" style={{ color: '#4A4A5A' }} />
          </button>
          <div>
            <h1 
              className="text-xl font-semibold"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
            >
              Crisis Resources
            </h1>
            <p 
              className="text-sm"
              style={{ fontFamily: 'Jost, sans-serif', color: '#6B6878' }}
            >
              You are not alone. Help is available.
            </p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-6 pb-32">
        {/* Important Notice */}
        <div 
          className="p-4 rounded-2xl mb-6"
          style={{ 
            background: 'linear-gradient(135deg, rgba(201,168,76,0.15) 0%, rgba(232,180,184,0.1) 100%)',
            border: '1px solid rgba(201,168,76,0.3)'
          }}
        >
          <p 
            className="text-sm leading-relaxed"
            style={{ fontFamily: 'Jost, sans-serif', color: '#2E2E42' }}
          >
            <strong style={{ color: '#C9A84C' }}>Important:</strong> Sage is an AI companion designed to support your healing journey, but <strong>Sage is not a therapist, counselor, or crisis service</strong>. If you are in immediate danger or experiencing a mental health emergency, please contact one of the resources below or call 911.
          </p>
        </div>

        {/* Crisis Resources List */}
        <div className="space-y-4">
          {CRISIS_RESOURCES.map((resource, index) => {
            const IconComponent = resource.icon;
            return (
              <div
                key={resource.name}
                className={`p-5 rounded-2xl ${resource.priority ? 'ring-2' : ''}`}
                style={{ 
                  backgroundColor: '#F9F4F0',
                  border: '1px solid #F0EBE4',
                  ringColor: resource.priority ? 'rgba(201,168,76,0.5)' : 'transparent'
                }}
                data-testid={`crisis-resource-${resource.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${resource.color}20` }}
                  >
                    <IconComponent className="w-6 h-6" style={{ color: resource.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 
                      className="text-lg font-medium mb-1"
                      style={{ fontFamily: 'Jost, sans-serif', color: '#1A1A2E' }}
                    >
                      {resource.name}
                    </h3>
                    <p 
                      className="text-sm mb-3"
                      style={{ fontFamily: 'Jost, sans-serif', color: '#6B6878' }}
                    >
                      {resource.description}
                    </p>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2">
                      {resource.phone && (
                        <button
                          onClick={() => handleCall(resource.phone)}
                          className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-[1.02]"
                          style={{ 
                            backgroundColor: `${resource.color}30`,
                            color: resource.color
                          }}
                          data-testid={`call-${index}`}
                        >
                          <Phone className="w-4 h-4" />
                          Call {resource.phone}
                        </button>
                      )}
                      {resource.textNumber && (
                        <button
                          onClick={() => handleText(resource.textNumber, resource.textKeyword)}
                          className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-[1.02]"
                          style={{ 
                            backgroundColor: '#F0EBE4',
                            color: '#2E2E42'
                          }}
                          data-testid={`text-${index}`}
                        >
                          <MessageCircle className="w-4 h-4" />
                          Text {resource.textKeyword ? `${resource.textKeyword} to ` : ''}{resource.textNumber}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-8 p-4 rounded-2xl" style={{ backgroundColor: '#FAF7F3' }}>
          <h4 
            className="text-sm font-medium mb-2"
            style={{ fontFamily: 'Jost, sans-serif', color: '#C9A84C' }}
          >
            When to reach out
          </h4>
          <ul 
            className="text-sm space-y-2"
            style={{ fontFamily: 'Jost, sans-serif', color: '#4A4A5A' }}
          >
            <li>• If you're having thoughts of hurting yourself or others</li>
            <li>• If you're in an unsafe situation at home</li>
            <li>• If you're experiencing overwhelming anxiety or panic</li>
            <li>• If you need someone to talk to right now</li>
          </ul>
        </div>

        {/* Footer */}
        <p 
          className="text-center text-xs mt-8"
          style={{ fontFamily: 'Jost, sans-serif', color: '#A3A0AE' }}
        >
          All resources listed are free and confidential.
          <br />
          Resources verified as of 2025.
        </p>
      </main>
    </div>
  );
};

export default CrisisResources;
